import React, { FC, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { i18n as I18n } from 'i18next';
import $ from 'jquery';
import Moment from 'moment';
import { extendMoment } from 'moment-range';

import { pipe } from 'fp-ts/function';
import * as R from 'fp-ts/Record';
import * as O from 'fp-ts/Option';

import CalendarIcon from '@bluevalet/react-icons/calendar';
import { Input } from '@bluevalet/common-ui';

import { isoLanguage, Language } from '@/core/lang/model';
import { TripDetail } from '@/modules/booking/model';

import { isErrorField } from '../utils';
import { State } from '../reducers';

const moment = extendMoment(Moment);

const getLang = (lang: string) =>
  pipe(
    isoLanguage,
    R.lookup(lang),
    O.getOrElse(() => isoLanguage[Language.English]),
  );

const pickerOptions = (i18n: I18n) => ({
  language: getLang(i18n.language),
  format: 'dd/mm/yyyy',
  startDate: new Date(),
  weekStart: 1,
  autoHide: true,
  startView: 0,
  endView: 0,
});

type DatePickerHTMLEvent = { date: Date };

interface Props {
  prefix: 'start' | 'end';
  date: Date | undefined;
  label: string;
  placeholder: string | undefined;
  forceValidation: boolean | undefined;
  onDateChange: (value: Date) => void;
  tripDetail: TripDetail | undefined;
}

const DayPicker: FC<Props> = ({ prefix, date, label, placeholder, forceValidation, onDateChange }) => {
  const { t, i18n } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const departure = useSelector((state: State) => state.booking.departure);
  const arrival = useSelector((state: State) => state.booking.arrival);

  // Re-initialize the datepicker whenever onDateChange, dates or language change.
  // onDateChange is stabilized via useCallback in Form.tsx so it only changes
  // when content meaningfully changes, keeping re-initializations to a minimum.
  useEffect(() => {
    const ref = inputRef.current;
    if (!ref) return;

    const $picker = $(ref);
    $picker.datepicker({
      ...pickerOptions(i18n),
      pick: (event: DatePickerHTMLEvent) => onDateChange(new Date(event.date)),
    });

    if (date) {
      $picker.datepicker('setDate', date, true);
      $picker.datepicker('setValue', $picker.datepicker('formatDate', date));
    }

    if (prefix === 'start' && arrival.date) $picker.datepicker('setEndDate', arrival.date);

    if (prefix === 'end' && departure.date) {
      $picker.datepicker('setStartDate', departure.date);
      if (!date) $picker.datepicker('setViewDate', departure.date);
    }

    return () => {
      $picker.datepicker('destroy');
    };
  }, [onDateChange, i18n, prefix, date, departure.date, arrival.date]);

  const onChangeDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const departureDate = moment(departure.date);
    const dateValue = moment(event.target.value, 'DD/MM/YYYY', true);
    if (dateValue.isValid() && dateValue.isSameOrAfter(departureDate, 'day')) {
      onDateChange(new Date(dateValue.toDate()));
    }
  };

  const hasErrors = isErrorField(forceValidation, date);
  const dateName = `${prefix}-date`;

  return (
    <div className="DayPicker">
      <Input
        ref={inputRef}
        data-cy={dateName}
        id={dateName}
        name="date"
        label={label}
        type="text"
        placeholder={placeholder || t('date')}
        autoComplete="off"
        onChange={onChangeDate}
        icon={<CalendarIcon />}
        inputMode="none"
        success={false}
        error={hasErrors}
        message={hasErrors ? t('error-required') : ''}
      />
    </div>
  );
};

export default DayPicker;
